import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCornerUpLeft, FiRefreshCw } from 'react-icons/fi';
import { readableError } from './api';
import MotionAnswerPanel from './MotionAnswerPanel';
import MotionLeaderboard from './MotionLeaderboard';
import { MotionEvidence, MotionViewer, useCompactMotionView } from './MotionReviewScreen';
import { getMotionReviewOutbox } from './motionReviewOutbox';
import { createMotionMediaBuffer } from './motionMediaBuffer';
import { MotionFlagActions, MotionMobileActions, MotionReviewRules, MotionReviewNotice } from './MotionReviewHelp';
import MotionCelebration, { useMotionCelebration } from './MotionCelebration';
import { analyticsErrorArea, trackCommunity } from './analytics';

const newRequestId = () => window.crypto?.randomUUID?.() || `motion-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
const isTyping = (target) => Boolean(target?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"]), .cr-motion-rules, .cr-motion-mobile-tools, video[controls]'));
const pendingIds = (box) => box.getSnapshot().jobs.map((job) => job.taskId || job.answer.taskId);
const STANDBY_CLIPS = 5;
const QUEUE_SIZE = STANDBY_CLIPS + 1;
const STANDBY_DECODERS = 2;
const REQUEST_TIMEOUT_MS = 20000;
const BACKGROUND_START_DELAY_MS = 1000;
const SEARCH_TIMEOUT_MESSAGE = 'Finding a clip is taking longer than expected. Please try again.';

async function withDeadline(request, timeoutMs) {
  let timer;
  try {
    return await Promise.race([request, new Promise((_, reject) => {
      timer = window.setTimeout(() => reject(new Error(SEARCH_TIMEOUT_MESSAGE)), timeoutMs);
    })]);
  } finally { window.clearTimeout(timer); }
}

export default function BufferedMotionReviewScreen({ api, onCountChange, onBusyChange, demo = false, showLeaderboard = false, reviewedCount, active = true }) {
  const [accuracy, setAccuracy] = useState(null);
  const scope = typeof api.scope === 'function' ? api.scope() : api.scope;
  const outbox = useMemo(() => getMotionReviewOutbox({ scope, api, isActive: () => !api.isActive || api.isActive(scope) }), [api, scope]);
  const [saveState, setSaveState] = useState(() => outbox.getSnapshot());
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [mediaFailed, setMediaFailed] = useState(false);
  const [refreshingClip, setRefreshingClip] = useState(false);
  const [operating, setOperating] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [enlarged, setEnlarged] = useState(false);
  const [generation, setGeneration] = useState(0);
  const [refreshGeneration, setRefreshGeneration] = useState(0);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const mediaWarmed = useRef(() => {});
  const { celebration, celebrate, dismissCelebration } = useMotionCelebration();
  const compact = useCompactMotionView();
  const mounted = useRef(false);
  const queue = useRef([]);
  const answered = useRef(new Set());
  const current = useRef(null);
  const busy = useRef(false);
  const fetching = useRef(null);
  const refillTimer = useRef(null);
  const refillAgain = useRef(false);
  const mediaCleanup = useRef(null);
  const previousPending = useRef(null);
  const lastUndoHandled = useRef(null);
  const lastRestoreHandled = useRef(null);
  const returningRestore = useRef(saveState.restoreRequestId);
  const automaticRefresh = useRef(null);
  const refreshing = useRef(false);
  const requestedPrevious = useRef(false);
  const firstPlayable = useRef(false);
  const searchPaused = useRef(false);
  const acknowledgements = useRef(0);
  const viewed = useRef(new Set());
  const callbacks = useRef({ onCountChange, onBusyChange });
  callbacks.current = { onCountChange, onBusyChange };
  const smallScreen = useRef(compact);
  smallScreen.current = compact;
  const media = useMemo(() => createMotionMediaBuffer({
    preloadEnabled: false, maxTasks: QUEUE_SIZE, warmCount: QUEUE_SIZE, concurrency: 2,
    onWarm: () => mediaWarmed.current(),
  }), []);
  const prepareUpcoming = useCallback(() => {
    if (!mounted.current) return;
    const next = smallScreen.current ? queue.current.filter((item) => item.id !== current.current?.id).slice(0, STANDBY_DECODERS).map((item) => media.decoderTask(item)).filter(Boolean) : [];
    setUpcomingTasks((previous) => previous.length === next.length && previous.every((item, index) => item.id === next[index].id && item.videoUrl === next[index].videoUrl) ? previous : next);
  }, [media]);
  mediaWarmed.current = prepareUpcoming;

  const display = useCallback((next) => {
    if (current.current?.id !== next?.id) automaticRefresh.current = null;
    current.current = next || null;
    setTask(next ? media.readyTask(next) : null);
    setLoaded(false); setFailed(false); setMediaFailed(false); setLoading(false); setEnlarged(false);
    setGeneration((value) => value + 1);
    prepareUpcoming();
  }, [media, prepareUpcoming]);

  const warm = useCallback(() => {
    const previous = outbox.getSnapshot().previousTask;
    media.syncTasks(queue.current, { includeMasks: !smallScreen.current, previousTasks: previous ? [previous] : [] });
    prepareUpcoming();
  }, [media, outbox, prepareUpcoming]);

  const restore = useCallback((restored, requestId) => {
    if (!restored?.videoUrl || !requestId || requestId === lastRestoreHandled.current) return;
    dismissCelebration();
    lastRestoreHandled.current = requestId;
    answered.current.delete(restored.id);
    queue.current = [restored, ...queue.current.filter((item) => item.id !== restored.id)].slice(0, QUEUE_SIZE);
    // Put the clip back in the upcoming queue before releasing its history pin.
    warm(); display(restored);
    setNotice('Last motion review undone. Please check this clip again.');
  }, [dismissCelebration, display, warm]);

  // A refill is shared by all triggers. Existing clips remain usable while it
  // runs; only an empty queue needs the loading screen.
  const refill = useCallback(async function refillBuffer({ refreshTaskId = null, previousOnly = false } = {}) {
    const refreshTarget = refreshTaskId ? current.current : null;
    // Give the first video a short head start. Playback readiness, failure, or
    // the fallback timer unlocks the five standby clips and Undo preview.
    if (!refreshTaskId && current.current && !firstPlayable.current) return null;
    if (fetching.current) {
      if (!refreshTaskId) { refillAgain.current = true; return fetching.current; }
      await fetching.current;
      if (!mounted.current) return null;
    }
    if (refreshTaskId && (current.current !== refreshTarget || busy.current)) return null;
    searchPaused.current = false;
    if (!current.current && !previousOnly) { setLoading(true); setError(''); }
    let continueScanning = false;
    let requestPreviousAfter = false;
    const work = async () => {
      const searchStarted = Date.now();
      acknowledgements.current = 0;
      let refreshed = null;
      let refreshAvailable = null;
      let refreshExclusions = [];
      try {
        for (let page = 0; page < 12 && mounted.current; page += 1) {
          const excluded = new Set([...refreshExclusions, ...queue.current.map((item) => item.id), ...pendingIds(outbox)]);
          if (refreshTaskId) excluded.delete(refreshTaskId);
          const searchingFirstClip = !refreshTaskId && !previousOnly && !queue.current.length;
          // The fast path scans only a handful of server candidates. Fall back
          // to the normal wider scan when that path repeatedly finds nothing.
          const startup = searchingFirstClip && page < 2;
          const limit = refreshTaskId || startup || previousOnly ? 1 : Math.max(1, QUEUE_SIZE - queue.current.length);
          const includeLastTask = !searchingFirstClip && !requestedPrevious.current && !outbox.getSnapshot().previousTask?.videoUrl;
          const remaining = current.current ? REQUEST_TIMEOUT_MS : Math.max(1, REQUEST_TIMEOUT_MS - (Date.now() - searchStarted));
          const result = await withDeadline(api.buffer({ limit, excludeTaskIds: [...excluded].slice(0, 200), ...(startup ? { startup: true } : {}), ...(includeLastTask ? { includeLastTask: true } : {}) }), remaining);
          if (refreshTaskId && Array.isArray(result.assignedTaskIds)) refreshAvailable = result.assignedTaskIds.includes(refreshTaskId);
          if (includeLastTask) requestedPrevious.current = true;
          if (!mounted.current || (refreshTaskId && (current.current !== refreshTarget || busy.current))) return null;
          await outbox.adoptServer({ reviewedCount: result.reviewedCount, lastSubmission: result.lastSubmission, lastTask: result.lastTask });
          if (!mounted.current || (refreshTaskId && (current.current !== refreshTarget || busy.current))) return null;
          const hidden = new Set([...pendingIds(outbox), ...answered.current]);
          const restoring = outbox.getSnapshot().restoreTask;
          if (restoring) hidden.delete(restoring.id);
          if (refreshTaskId) hidden.delete(refreshTaskId);
          const available = new Map(queue.current.filter((item) => !hidden.has(item.id)).map((item) => [item.id, item]));
          const oldSize = available.size;
          for (const item of result.tasks || []) {
            if (!hidden.has(item.id)) available.set(item.id, item);
            if (item.id === refreshTaskId) refreshed = item;
          }
          // The server orders the reserved queue, including the regular mix of
          // triggers. Keep the clip already on screen in place while warming.
          let ordered = result.assignedTaskIds
            ? result.assignedTaskIds.map((id) => available.get(id)).filter(Boolean)
            : [...available.values()];
          const visible = current.current;
          if (visible && !hidden.has(visible.id) && !refreshTaskId) ordered = [visible, ...ordered.filter((item) => item.id !== visible.id)];
          queue.current = ordered.slice(0, QUEUE_SIZE);
          warm();
          continueScanning = !refreshTaskId && !queue.current.length && result.scanPending === true;
          if (!current.current && !busy.current) {
            if (continueScanning) setLoading(true);
            else display(queue.current[0] || null);
          }
          if (searchingFirstClip && !queue.current.length && !continueScanning) {
            // A confirmed empty queue has no video load event. Show caught-up
            // immediately, then prepare the previous answer so Undo still works.
            media.setPreloadEnabled(true);
            requestPreviousAfter = Boolean(result.lastSubmission && !requestedPrevious.current && !outbox.getSnapshot().previousTask?.videoUrl);
          }
          setError('');
          // A newly reserved clip can precede the requested one. Retry once
          // excluding all other reservations before declaring a refresh failed.
          if (refreshTaskId && !refreshed && refreshAvailable === true && page === 0) {
            refreshExclusions = result.assignedTaskIds.filter((id) => id !== refreshTaskId);
            continue;
          }
          if (queue.current.length && !firstPlayable.current) break;
          if (refreshing.current && !refreshTaskId) break;
          if (refreshTaskId || previousOnly || queue.current.length >= QUEUE_SIZE) break;
          if (available.size <= oldSize && !result.scanPending) break;
          if (!result.hasMore && !result.scanPending && !(result.tasks || []).length) break;
        }
        // scanPending is not an empty queue. Bound repeated searches so a
        // stalled scan has an honest recovery state instead of spinning forever.
        if (continueScanning) throw new Error(SEARCH_TIMEOUT_MESSAGE);
        return refreshTaskId ? { task: refreshed, available: refreshAvailable } : null;
      } catch (requestError) {
        continueScanning = false;
        requestPreviousAfter = false;
        if (mounted.current) {
          setError(readableError(requestError));
          if (!current.current) { searchPaused.current = true; setLoading(false); }
        }
        return null;
      }
    };
    fetching.current = work();
    try { return await fetching.current; }
    finally {
      fetching.current = null;
      if ((continueScanning || requestPreviousAfter) && mounted.current && !current.current) {
        window.clearTimeout(refillTimer.current);
        refillTimer.current = window.setTimeout(() => { if (mounted.current) refillBuffer({ previousOnly: requestPreviousAfter }); }, 250);
      }
    }
  }, [api, display, media, outbox, warm]);

  const scheduleRefill = useCallback((force = false) => {
    if (!force && acknowledgements.current < STANDBY_CLIPS && queue.current.length >= QUEUE_SIZE) return;
    window.clearTimeout(refillTimer.current);
    refillTimer.current = window.setTimeout(() => { if (mounted.current && !busy.current) refill(); }, 250);
  }, [refill]);

  const startBackground = useCallback(() => {
    if (firstPlayable.current) return;
    firstPlayable.current = true;
    media.setPreloadEnabled(true);
    scheduleRefill(true);
  }, [media, scheduleRefill]);

  useEffect(() => {
    if (!task || firstPlayable.current || (saveState.restoreTask?.id === task.id && saveState.undoPending)) return undefined;
    // Give the visible video a short head start, but do not make a stalled iOS
    // loadeddata event hold every standby download hostage.
    const timer = window.setTimeout(startBackground, BACKGROUND_START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [saveState.restoreTask, saveState.undoPending, startBackground, task]);

  useEffect(() => {
    if (!task || loaded || failed || refreshingClip) return undefined;
    const timer = window.setTimeout(() => { setFailed(true); setMediaFailed(true); }, REQUEST_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [failed, loaded, refreshingClip, task]);

  useEffect(() => {
    mounted.current = true;
    window.clearTimeout(mediaCleanup.current);
    const unsubscribe = outbox.subscribe((state) => {
      if (!mounted.current) return;
      setSaveState(state);
      if (typeof state.reviewedCount === 'number') callbacks.current.onCountChange?.(Math.max(0, state.reviewedCount + (state.pendingReviewedCount ?? state.pendingCount) + (state.countDelta || 0)));
      if (state.restoreTask) restore(state.restoreTask, state.restoreRequestId);
      if (previousPending.current !== null && state.pendingCount < previousPending.current) {
        acknowledgements.current += previousPending.current - state.pendingCount;
        scheduleRefill();
      }
      previousPending.current = state.pendingCount;
      if (state.lastUndo && state.lastUndo.requestId !== lastUndoHandled.current) {
        lastUndoHandled.current = state.lastUndo.requestId;
        // Restoration already made this clip reviewable. Its replacement may
        // have been entered before this ACK, so keep that newer answer hidden.
        scheduleRefill(true);
      }
    });
    outbox.configure({ onReconcile: (answer, result) => {
      if (!mounted.current) return;
      if (result.reason === 'submission-undone') {
        answered.current.delete(answer.taskId);
        scheduleRefill(true);
        if (result.accepted === false) setNotice('An answer was undone in another session. Your progress has been updated.');
      }
      // Unavailable tracks are quietly acknowledged by the outbox; the next
      // clip is already on screen and later saved answers can keep syncing.
    } });
    Promise.resolve(outbox.refresh()).catch((requestError) => { if (mounted.current) setError(readableError(requestError)); });
    refill();
    const online = () => { outbox.retry().catch(() => {}); scheduleRefill(true); };
    const timer = window.setInterval(() => {
      if (!searchPaused.current && (refillAgain.current || queue.current.length < QUEUE_SIZE)) { refillAgain.current = false; scheduleRefill(true); }
    }, 30000);
    window.addEventListener('online', online);
    return () => {
      mounted.current = false; unsubscribe(); window.clearTimeout(refillTimer.current);
      window.clearInterval(timer); window.removeEventListener('online', online);
      mediaCleanup.current = window.setTimeout(() => media.dispose(), 0);
      callbacks.current.onBusyChange?.(false);
    };
  }, [media, outbox, refill, restore, scheduleRefill]);
  useEffect(() => {
    warm();
    if (current.current) setTask(media.readyTask(current.current));
  }, [compact, media, warm]);
  useEffect(() => { callbacks.current.onBusyChange?.(active && (operating || undoing)); }, [active, operating, undoing]);
  useEffect(() => { if (!active) setEnlarged(false); }, [active]);
  useEffect(() => {
    if (!active || !task || !loaded || viewed.current.has(task.id)) return;
    viewed.current.add(task.id);
    trackCommunity('motion_clip_viewed', {
      has_boxes: Boolean(task.boxes?.length),
      has_mask: Boolean(task.maskVideoUrl),
      has_full_clip: Boolean(task.fullClipUrl),
      device_layout: compact ? 'mobile' : 'desktop',
    });
  }, [active, compact, loaded, task]);

  const submit = useCallback(async (verdict, flaggedAs = null, inputMethod = 'button') => {
    const selected = current.current;
    if (!active || !selected || busy.current || !loaded || failed || enlarged) return;
    busy.current = true; setOperating(true); setError('');
    const answer = { taskId: selected.id, requestId: newRequestId(), verdict,
      speciesId: null,
      flags: { predator: flaggedAs === 'predator', interesting: flaggedAs === 'interesting' },
      ...(selected.undoToken ? { undoToken: selected.undoToken } : {}) };
    try {
      // This promise waits only for the local durable write, never the server.
      await outbox.submit(answer, selected);
      trackCommunity('motion_review_submitted', {
        verdict,
        flag: flaggedAs || 'none',
        has_species: false,
        input_method: inputMethod,
      });
      if (!mounted.current) return;
      celebrate(answer);
      media.retainPrevious(selected);
      answered.current.add(selected.id);
      queue.current = queue.current.filter((item) => item.id !== selected.id);
      warm();
      display(queue.current[0] || null);
      setNotice('');
      if (!queue.current.length) setLoading(true);
      if (!compact) window.scrollTo?.({ top: 0, behavior: 'instant' });
    } catch (requestError) {
      trackCommunity('community_action_error', { action: 'motion_submit', error_code: analyticsErrorArea(requestError) });
      if (mounted.current) setError(`Your answer could not be kept on this device. ${readableError(requestError)}`);
    } finally {
      busy.current = false;
      if (mounted.current) { setOperating(false); scheduleRefill(); }
    }
  }, [active, celebrate, compact, display, enlarged, failed, loaded, media, outbox, scheduleRefill, warm]);

  const undo = useCallback(async () => {
    const latest = outbox.getSnapshot();
    if (!active || busy.current || !latest.canUndo) return;
    dismissCelebration();
    busy.current = true; setUndoing(true); setError(''); setEnlarged(false);
    try {
      const result = await outbox.undoLatest();
      trackCommunity('motion_review_undone', { input_method: 'button_or_keyboard' });
      if (!mounted.current || !result) return;
      restore(result.task, outbox.getSnapshot().restoreRequestId || result.undoRequestId || result.requestId);
    } catch (requestError) { trackCommunity('community_action_error', { action: 'motion_undo', error_code: analyticsErrorArea(requestError) }); if (mounted.current) setError(readableError(requestError)); }
    finally { busy.current = false; if (mounted.current) { setUndoing(false); scheduleRefill(); } }
  }, [active, dismissCelebration, outbox, restore, scheduleRefill]);

  const cancelUndo = useCallback(async () => {
    try {
      const result = await outbox.cancelFailedUndo();
      if (!mounted.current) return;
      const discarded = new Set(result?.discardedTaskIds || []);
      if (result?.canceledTaskId) discarded.add(result.canceledTaskId);
      for (const id of discarded) answered.current.add(id);
      queue.current = queue.current.filter((item) => !discarded.has(item.id));
      warm();
      if (discarded.has(current.current?.id)) display(queue.current[0] || null);
      setNotice(result?.notice || 'Your previous answer has been kept. Continue with the next clip.');
      scheduleRefill(true);
    } catch (requestError) { if (mounted.current) setError(readableError(requestError)); }
  }, [display, outbox, scheduleRefill, warm]);

  const refresh = useCallback(async () => {
    if (busy.current || refreshing.current) return;
    const selected = current.current;
    if (!selected) return refill();
    const before = outbox.getSnapshot();
    const restoreRequestId = before.restoreTask?.id === selected.id ? before.restoreRequestId : null;
    // A pending Undo can still be absent from the server's reserved queue.
    // Its acknowledgement will trigger automatic recovery if it is needed.
    if (restoreRequestId && before.undoPending) return;
    refreshing.current = true; setRefreshingClip(true);
    setEnlarged(false);
    try {
      const result = await refill({ refreshTaskId: selected.id });
      if (!mounted.current || current.current !== selected || !result) return;
      if (!result.task && result.available !== false) {
        setError('This clip could not be refreshed. Please try again.');
        return;
      }
      if (restoreRequestId && !await outbox.reconcileRestoration({ requestId: restoreRequestId, task: result.task })) return;
      if (!mounted.current || current.current !== selected) return;
      media.forgetPresentation?.(selected.id);
      setRefreshGeneration((value) => value + 1);
      if (result.task) {
        display(result.task, { preserveAnswer: true });
      } else {
        answered.current.add(selected.id);
        queue.current = queue.current.filter((item) => item.id !== selected.id);
        warm(); display(queue.current[0] || null);
        setNotice('That track is no longer available for review. Moving to the next clip.');
        if (!queue.current.length) setLoading(true);
        startBackground(); scheduleRefill(true);
      }
    } catch (requestError) {
      if (mounted.current) setError(readableError(requestError));
    } finally {
      refreshing.current = false;
      if (mounted.current) setRefreshingClip(false);
    }
  }, [display, media, outbox, refill, scheduleRefill, startBackground, warm]);

  useEffect(() => {
    if (!task || operating || undoing || refreshingClip) return;
    if (saveState.restoreTask?.id === task.id && saveState.undoPending) return;
    const returning = returningRestore.current && returningRestore.current === saveState.restoreRequestId;
    if ((!returning && !mediaFailed) || automaticRefresh.current === task.id) return;
    automaticRefresh.current = task.id;
    returningRestore.current = null;
    void refresh();
  }, [mediaFailed, operating, refresh, refreshingClip, saveState.restoreRequestId, saveState.restoreTask, saveState.undoPending, task, undoing]);

  useEffect(() => {
    const keyDown = (event) => {
      if (!active || event.defaultPrevented || event.repeat || event.isComposing || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || isTyping(event.target) || busy.current || enlarged) return;
      const key = event.code === 'Space' || event.key === 'Spacebar' ? ' ' : event.key?.toLowerCase();
      if (key === 'u' && saveState.canUndo) { event.preventDefault(); undo(); return; }
      if (!task || !loaded || failed) return;
      if (['s', 'n', 'p', 'h', ' '].includes(key)) {
        event.preventDefault();
        submit(key === ' ' ? 'unsure' : key === 'n' ? 'false_alarm' : 'animal', key === 'p' ? 'predator' : key === 'h' ? 'interesting' : null, 'keyboard');
      }
    };
    document.addEventListener('keydown', keyDown);
    return () => document.removeEventListener('keydown', keyDown);
  }, [active, enlarged, failed, loaded, saveState.canUndo, submit, task, undo]);

  const closeEnlarged = useCallback(() => setEnlarged(false), []);
  const disabled = !active || operating || undoing || !loaded || failed;
  const undoButton = <button className="cr-undo" type="button" aria-label="Undo last motion review" aria-keyshortcuts="U" disabled={!active || !saveState.canUndo || operating || undoing} onClick={undo}><FiCornerUpLeft aria-hidden="true" />{undoing ? 'Undoing…' : 'Undo'} <kbd aria-hidden="true">U</kbd></button>;
  return <main className="cr-main cr-motion-main">
    <MotionCelebration celebration={celebration} />
    <div className="cr-intro"><h1>Help us spot real movement</h1><MotionReviewRules compact={compact} /></div>
    {showLeaderboard && <MotionLeaderboard onAccuracyChange={setAccuracy} api={api} reviewedCount={reviewedCount} compact={compact} hidePersonalCount={compact && Boolean(task)} />}
    <MotionReviewNotice notice={notice} />
    {demo && <p className="cr-demo-label">Design preview · Reviews stay in this browser and are not submitted.</p>}
    {(saveState.error || saveState.storageError) && <p className="cr-error" role="alert">{saveState.storageError || saveState.error} <button type="button" className="cr-text-button" onClick={() => outbox.retry().catch((requestError) => setError(readableError(requestError)))}>Retry saving</button></p>}
    {saveState.canCancelUndo && <button className="cr-text-button" type="button" onClick={cancelUndo}>Keep previous answer and continue</button>}
    {error && <p className="cr-error" role="alert">{error} <button type="button" className="cr-text-button" onClick={refresh} disabled={operating || undoing}>Refresh clips</button></p>}
    {loading ? <div className="cr-empty" role="status"><h2>Finding your next motion clip…</h2></div> : !task ? <div className="cr-empty">
      <h2>{error ? 'We couldn’t load your next clip' : 'You’re caught up on motion'}</h2>
      <p>{error ? 'Please check your connection and try again. Your saved answers are safe.' : saveState.pendingCount ? 'Your answers are saving in the background. More clips will appear as the queue updates.' : 'You’ve reviewed all the motion clips currently available to you. Come back soon for more.'}</p>
      <div className="cr-empty-actions"><button className="cr-primary" disabled={operating || undoing} onClick={() => refill()}><FiRefreshCw aria-hidden="true" />Check for new clips</button>{undoButton}</div>
    </div> : <div className="cr-review-layout">
      <section className="cr-photo-section" aria-label="Motion evidence">
        <MotionEvidence key={compact ? `mobile-motion-deck-${refreshGeneration}` : `${task.id}-${generation}`} task={task} active={active} upcomingTasks={upcomingTasks} presentationKey={generation} showSpottedTime accuracy={accuracy} reviewedCount={showLeaderboard ? reviewedCount : undefined} onRefresh={refresh} onEnlarge={() => setEnlarged(true)} onLoad={() => { setLoaded(true); setFailed(false); startBackground(); }} onError={() => { setFailed(true); setMediaFailed(true); setLoaded(false); startBackground(); }} onMaskError={() => setMediaFailed(true)} />
        <p className="cr-motion-hint"><span className="cr-motion-desktop-hint">Count any animal or bird moving on the ground or in a tree, including outside the highlighted box. </span>The clip repeats so you can take another look.</p>
        {refreshingClip ? <p className="cr-small cr-muted" role="status">Refreshing the clip…</p> : !loaded && !failed && <p className="cr-small cr-muted" role="status">Loading the clip…</p>}
        {failed && <p className="cr-error" role="alert">This clip couldn’t load. <button className="cr-text-button" onClick={refresh} disabled={operating || undoing}>Refresh clip</button></p>}
      </section>
      <MotionAnswerPanel>
        {!compact && <MotionFlagActions disabled={disabled} onFlag={(flag) => submit('animal', flag, 'button')} />}
        <div className="cr-motion-submit" role="group" aria-label="Motion review controls">
          {compact ? <MotionMobileActions disabled={disabled} onSubmit={submit} undoButton={undoButton} /> : <>
          <div className="cr-motion-verdicts" role="group" aria-label="Motion review answer">
            {[['animal', 'Real motion', 'S'], ['false_alarm', 'False alarm', 'N'], ['unsure', 'Not sure', 'Space']].map(([value, label, shortcut]) => <button className={`cr-verdict ${value === 'animal' ? 'cr-motion-animal' : ''}`} type="button" key={value} aria-keyshortcuts={shortcut} disabled={disabled} onClick={() => submit(value)}>{label}<kbd aria-hidden="true">{shortcut}</kbd></button>)}
          </div>
          <div className="cr-motion-bottom"><span className="cr-small cr-muted">Unsure? Don’t guess.</span>{undoButton}</div>
          </>}
        </div>
      </MotionAnswerPanel>
    </div>}
    {enlarged && task && <MotionViewer task={task} onClose={closeEnlarged} onRefresh={refresh} />}
  </main>;
}
